import { useSyncExternalStore } from "react"
import { DEFAULT_LANGUAGE_CODE } from "@/v2-common/constants"

/**
 * A BCP-47-ish language tag, matching `common.Languages.Code` in the features
 * DB (e.g. "en-GB", "nb-NO", "sv-SE").
 */
export type LanguageTag = string

/**
 * The canonical base language. The blob/DB guarantees it exists for every
 * translatable object (`common.LanguageJson` consumers fall back to
 * `$."en-GB"`, and every promoted code text seeds en-GB first), so bundled
 * catalogs treat it as the last-resort fallback for any missing translation.
 */
export const FALLBACK_LANGUAGE_TAG = "en-GB"

/**
 * Module-level locale store for bundled UI text. Deliberately NOT a React
 * context: base primitives (v2-ui) render on screens that mount outside the
 * console's providers (login, error boundaries, tests, other apps), so the
 * locale must be readable synchronously with no ancestor. The host app pushes
 * the active language in via `setLocale` (see AppContextLoader); until then it
 * is the app default (`nb-NO`). Components subscribe through `useLocale` so a
 * language switch re-renders them.
 */
let currentLocale: LanguageTag = DEFAULT_LANGUAGE_CODE
const listeners = new Set<() => void>()

export function getLocale(): LanguageTag {
  return currentLocale
}

export function setLocale(locale: LanguageTag): void {
  if (!locale || locale === currentLocale) return
  currentLocale = locale
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Subscribe non-React side-effect targets to locale changes (ArcGIS intl,
 * number/date formatting, persistence). The host app registers these ONCE at
 * bootstrap, after which `setLocale` is the single verb for switching
 * language anywhere — every effect follows automatically. Fires only on
 * actual change (`setLocale` no-ops on the same tag); returns an
 * unsubscribe function.
 */
export function onLocaleChange(listener: () => void): () => void {
  return subscribe(listener)
}

export function useLocale(): LanguageTag {
  return useSyncExternalStore(subscribe, getLocale, getLocale)
}

/**
 * The language tags a catalog actually ships translations for — the union
 * of per-key language tags. This (not a hardcoded list) is what a pre-login
 * language selector should offer: a tag with no bundled entries would
 * render as `en-GB` fallback anyway. Post-login the DB `common.Languages`
 * rows are authoritative instead. A translation PR that adds a language to
 * the catalogs extends the selector automatically — expanding into a new
 * market means shipping its translations, nothing to register.
 */
export function collectOfflineCatalogTags(
  catalogs: readonly Record<string, Partial<Record<string, unknown>>>[],
): LanguageTag[] {
  const tags = new Set<LanguageTag>()
  for (const catalog of catalogs) {
    for (const entry of Object.values(catalog)) {
      for (const tag of Object.keys(entry)) tags.add(tag)
    }
  }
  return [...tags]
}

/**
 * Initial-locale inference, run once at app bootstrap BEFORE render:
 *
 * 1. `stored` — the visitor's persisted choice (the host app's
 *    localStorage, written back on every applied locale — including a
 *    pre-login pick in the login page's language selector); trusted
 *    verbatim since it was a valid tag when saved, including DB-only
 *    languages the bundled catalogs don't know about.
 * 2. `DEFAULT_LANGUAGE_CODE` (nb-NO). Deliberately NOT the browser
 *    language: the customer base is Norwegian and an English OS/browser is
 *    ambient there, so browser inference would mostly guess wrong.
 *    Non-Norwegian first-time visitors opt in via the login page selector
 *    instead, which persists their choice for every later visit.
 *
 * The DB user's `ActiveLanguageCode` remains authoritative once loaded —
 * the host app calls `setLocale` with it, which also re-persists.
 */
export function inferInitialLocale(
  stored: string | null | undefined,
): LanguageTag {
  return stored || DEFAULT_LANGUAGE_CODE
}
